import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  PlusIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { BoltIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import { useAuth } from '../contexts/AuthContext';
import { useDreamscape } from '../contexts/DreamscapeContext';
import { getDailyQuote } from '../utils/dreamscape';
import chatNexusLogo from '../assets/ChatNexus_Logo.png';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface DashboardStats {
  companionCount: number;
  activeConversations: number;
  conversationCount: number;
  subscriptionPlan: string;
}

interface RecentCompanion {
  id: string;
  name: string;
  avatarUrl?: string;
  lastMessageAt?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { timeOfDay } = useDreamscape();
  const dailyQuote = getDailyQuote();
  const [stats, setStats] = useState<DashboardStats>({
    companionCount: 0,
    activeConversations: 0,
    conversationCount: 0,
    subscriptionPlan: 'free',
  });
  const [recentCompanions, setRecentCompanions] = useState<RecentCompanion[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch user stats
      const [tokenResponse, companionsResponse] = await Promise.all([
        axios.get('/tokens/balance').catch(() => ({ data: { currentBalance: 0 } })),
        axios.get('/companions').catch(() => ({ data: [] })),
      ]);

      // Count conversations across all companions
      let totalConversations = 0;
      try {
        const convResponse = await axios.get('/chat/conversations');
        totalConversations = convResponse.data.length || 0;
      } catch {
        totalConversations = 0;
      }

      setStats({
        companionCount: companionsResponse.data.length || 0,
        activeConversations: companionsResponse.data.filter((c: any) => c.isActive).length || 0,
        conversationCount: totalConversations,
        subscriptionPlan: tokenResponse.data.subscriptionPlan || 'free',
      });

      setRecentCompanions(companionsResponse.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const getEmojiAvatar = (avatarUrl?: string) => {
    if (avatarUrl && avatarUrl.length <= 4) {
      return avatarUrl;
    }
    return '🌟';
  };

  const getGreeting = () => {
    switch (timeOfDay) {
      case 'morning':
        return t('dashboard.goodMorning', 'Good morning');
      case 'afternoon':
        return t('dashboard.goodAfternoon', 'Good afternoon');
      case 'evening':
        return t('dashboard.goodEvening', 'Good evening');
      case 'night':
        return t('dashboard.goodNight', 'Good night');
    }
  };

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header with Greeting */}
        <div className="p-6 pb-4">
          <img src={chatNexusLogo} alt="Chat Nexus" className="cn-logo-adaptive h-7 w-auto mb-5" />
          <h1
            className="text-[28px] font-semibold font-cormorant"
            style={{ color: 'var(--ds-text-primary)' }}
          >
            {getGreeting()}, {user?.name || t('dashboard.defaultName', 'there')}
          </h1>
          <p className="font-cormorant italic mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
            {t('dashboard.welcomeBackDreamscape', 'Welcome back to your world')}
          </p>
        </div>

        {/* Quick Actions */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate(stats.companionCount > 0 ? '/chat' : '/companion/name')}
              className="p-4 bg-primary-500 hover:bg-primary-600 rounded-2xl transition-colors"
            >
              <ChatBubbleLeftRightIcon className="w-8 h-8 text-white mb-2" />
              <div className="text-white font-semibold">
                {stats.companionCount > 0
                  ? t('dashboard.startChat')
                  : t('dashboard.createCompanion', 'Create Companion')}
              </div>
            </button>
            <button
              onClick={() => navigate('/companion/name')}
              className="p-4 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-100 rounded-2xl transition-colors"
            >
              <PlusIcon className="w-8 h-8 text-dark-text dark:text-dark-text text-light-text mb-2" />
              <div className="text-dark-text dark:text-dark-text text-light-text font-semibold">
                {t('dashboard.newCompanion', 'New Companion')}
              </div>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="px-6 pb-4">
          <div
            className="ds-card rounded-2xl p-4"
            style={{
              background: 'var(--ds-bg-card)',
              border: '1px solid var(--ds-border)',
            }}
          >
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                  {stats.companionCount}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('companions.title')}
                </div>
              </div>
              <div className="text-center border-l" style={{ borderColor: 'var(--ds-border)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--ds-text-primary)' }}>
                  {stats.conversationCount || 0}
                </div>
                <div className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)' }}>
                  {t('dashboard.conversations', 'Conversations')}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Status */}
        <div className="px-6 pb-4">
          <button
            onClick={() => navigate('/subscription')}
            className="w-full p-4 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl transition-all hover:shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BoltIcon className="w-6 h-6 text-white" />
                <div className="text-left">
                  <div className="text-white font-semibold">
                    {stats.subscriptionPlan && stats.subscriptionPlan !== 'free'
                      ? `${stats.subscriptionPlan.charAt(0).toUpperCase() + stats.subscriptionPlan.slice(1)} Plan`
                      : t('dashboard.explorePlans', 'Explore Plans')}
                  </div>
                  <div className="text-white/80 text-sm">
                    {stats.subscriptionPlan && stats.subscriptionPlan !== 'free'
                      ? t('dashboard.managePlan', 'Manage your subscription')
                      : t('dashboard.findPerfectPlan', 'Find the perfect plan for you')}
                  </div>
                </div>
              </div>
              <ArrowRightIcon className="w-5 h-5 text-white" />
            </div>
          </button>
        </div>

        {/* Recent Companions */}
        {recentCompanions.length > 0 && (
          <div className="px-6 pb-4">
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-lg font-semibold font-cormorant"
                style={{ color: 'var(--ds-text-primary)' }}
              >
                {t('dashboard.yourCompanions')}
              </h2>
              <button
                onClick={() => navigate('/companions')}
                className="text-primary-500 text-sm font-medium"
              >
                {t('common.seeAll', 'See all')}
              </button>
            </div>
            <div className="space-y-2">
              {recentCompanions.map((companion) => (
                <button
                  key={companion.id}
                  onClick={() => navigate(`/chat/${companion.id}`)}
                  className="w-full p-3 ds-card rounded-xl transition-colors hover:opacity-90"
                  style={{
                    background: 'var(--ds-bg-card)',
                    border: '1px solid var(--ds-border)',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center text-lg">
                      {getEmojiAvatar(companion.avatarUrl)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium" style={{ color: 'var(--ds-text-primary)' }}>
                        {companion.name}
                      </div>
                      <div className="text-sm" style={{ color: 'var(--ds-text-secondary)' }}>
                        {t('dashboard.tapToChat', 'Tap to chat')}
                      </div>
                    </div>
                    <ArrowRightIcon
                      className="w-4 h-4"
                      style={{ color: 'var(--ds-text-secondary)' }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="px-6 pb-6">
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/subscription')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-100 rounded-xl transition-colors"
            >
              <BoltIcon className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
              <div className="text-xs text-dark-text dark:text-dark-text text-light-text">
                {t('dashboard.plans', 'Plans')}
              </div>
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-100 rounded-xl transition-colors"
            >
              <SparklesIcon className="w-6 h-6 text-primary-500 mx-auto mb-1" />
              <div className="text-xs text-dark-text dark:text-dark-text text-light-text">
                {t('navigation.profile')}
              </div>
            </button>
            <button
              onClick={() => navigate('/billing')}
              className="p-3 bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-border dark:hover:bg-dark-border hover:bg-gray-100 rounded-xl transition-colors"
            >
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-500 mx-auto mb-1" />
              <div className="text-xs text-dark-text dark:text-dark-text text-light-text">
                {t('dashboard.history', 'History')}
              </div>
            </button>
          </div>
        </div>

        {/* Literary Quote */}
        <div className="px-6 pb-8 text-center">
          <p
            className="font-cormorant italic text-base leading-relaxed"
            style={{ color: 'var(--ds-text-secondary)' }}
          >
            &ldquo;{dailyQuote.text}&rdquo;
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--ds-text-secondary)', opacity: 0.7 }}>
            — {dailyQuote.author}
          </p>
        </div>
      </div>
    </MobileLayout>
  );
};

export default Dashboard;
